"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseRegistryManifest = void 0;
var editor_elements_conventions_1 = require("@wix/editor-elements-conventions");
var assets_1 = require("../toolbox/assets");
var RegistryError_1 = require("../errors/RegistryError");
var libraryManifestBuilder_1 = require("./builders/libraryManifestBuilder");
function getDepsURLs(indexes, shared) {
    if (shared === void 0) { shared = []; }
    if (indexes.some(function (index) { return !shared[index]; })) {
        throw RegistryError_1.createInternalException('Wrong Manifest Structure').withDetails('There is link to the shared chunk, but chunk does not exist');
    }
    return indexes.map(function (index) { return shared[index]; });
}
function extractURLs(urls, shared) {
    if (shared === void 0) { shared = []; }
    var model = {};
    urls.forEach(function (url) {
        if (Array.isArray(url)) {
            model.deps = getDepsURLs(url, shared);
        }
        else {
            var extension = assets_1.getExtension(url);
            if (assets_1.isJS(extension)) {
                model.js = url;
            }
            else if (assets_1.isCSS(extension)) {
                model.css = url;
            }
        }
    });
    return model;
}
function addComponentModels(hostBuilder, _a) {
    var componentName = _a.componentName, model = _a.model;
    var componentBuilder = hostBuilder.getComponentBuilder(componentName);
    if (model.js) {
        componentBuilder.addUrl(model.id, model.js);
    }
    if (model.css) {
        componentBuilder.addStyleAsset(model.css);
    }
    if (model.deps) {
        componentBuilder.addDeps(model.deps);
    }
}
function addBatchPart(hostBuilder, _a) {
    var componentName = _a.componentName, part = _a.part, model = _a.model;
    var componentBuilder = hostBuilder.getComponentBuilder(componentName);
    /**
     * remove all prev. added assets that leads to the default part bundle
     */
    componentBuilder.filterAssets(function (asset) { return asset.part !== part; });
    if (model.js) {
        componentBuilder.addPart(model.id, part, model.js, true);
    }
    if (model.css) {
        componentBuilder.addStyleAsset(model.css, part);
    }
    if (model.deps) {
        componentBuilder.addDeps(model.deps);
    }
}
function parseRegistryManifest(_a) {
    var topology = _a.topology, manifest = _a.manifest;
    var baseURL = manifest.baseURL.endsWith('/')
        ? manifest.baseURL
        : manifest.baseURL + "/";
    var libraryBuilder = libraryManifestBuilder_1.createLibraryManifestBuilder({
        topology: topology,
    });
    var buildComponentId = function (componentName, part) {
        return editor_elements_conventions_1.getBundleId({
            componentName: componentName,
            part: part,
            namespace: manifest.namespace,
            host: manifest.host,
        });
    };
    var buildBatchId = function (batch) {
        return editor_elements_conventions_1.getBundleId({
            batch: batch,
            namespace: manifest.namespace,
            host: manifest.host,
        });
    };
    var withBaseUrl = function (bundle) {
        return "" + baseURL + bundle;
    };
    var buildURL = function (id, hash) {
        if (!hash || !id) {
            return undefined;
        }
        return withBaseUrl(id + "." + hash);
    };
    if (manifest.environment) {
        if (manifest.environment.runtime) {
            libraryBuilder.addWebpackRuntimeBundle(buildURL('webpack-runtime', manifest.environment.runtime));
        }
        if (manifest.environment.hot) {
            libraryBuilder.addHotBundle(buildURL('hot', manifest.environment.hot));
        }
    }
    var hostBuilder = libraryBuilder.getHostBuilder(manifest.host);
    if (manifest.statics) {
        var statics_1 = manifest.statics;
        Object.keys(statics_1).forEach(function (componentName) {
            hostBuilder.addComponentStatics(componentName, __assign({}, statics_1[componentName]));
        });
    }
    if (manifest.libraryStatics) {
        var libraryStatics = manifest.libraryStatics;
        libraryBuilder.addLibraryStatics(libraryStatics);
    }
    if (manifest.model) {
        var _b = manifest.model, modelHash = _b[0], stylesHash = _b[1];
        var id = editor_elements_conventions_1.getBundleId({
            namespace: manifest.namespace,
            host: manifest.host,
        });
        var modelURL = buildURL(id, modelHash);
        var stylesURL = buildURL(id, stylesHash);
        if (modelURL) {
            hostBuilder.addUrl(id, modelURL);
        }
        if (stylesURL) {
            hostBuilder.addStyleAsset(stylesURL);
        }
        if (manifest.deps) {
            hostBuilder.addDeps(getDepsURLs(manifest.deps, manifest.shared).map(withBaseUrl));
        }
    }
    if (manifest.assets) {
        var assets = manifest.assets;
        assets.forEach(function (asset) {
            var type = asset[0], url = asset[1];
            if (url && type) {
                var assetsURL = "" + baseURL + url;
                var extension = assets_1.getExtension(assetsURL);
                libraryBuilder.addLibraryAsset(assetsURL, type, extension);
            }
        });
    }
    if (manifest.components) {
        var components_1 = manifest.components;
        Object.keys(components_1).forEach(function (componentName) {
            var _a;
            var model = extractURLs(components_1[componentName], manifest.shared);
            var id = buildComponentId(componentName);
            addComponentModels(hostBuilder, {
                componentName: componentName,
                model: {
                    id: id,
                    js: buildURL(id, model.js),
                    css: buildURL(id, model.css),
                    deps: (_a = model.deps) === null || _a === void 0 ? void 0 : _a.map(withBaseUrl),
                },
            });
        });
    }
    if (manifest.parts) {
        var parts_1 = manifest.parts;
        Object.keys(parts_1).forEach(function (componentName) {
            Object.keys(parts_1[componentName]).forEach(function (part) {
                var componentBuilder = hostBuilder.getComponentBuilder(componentName);
                var _a = parts_1[componentName][part], modelHash = _a[0], stylesHash = _a[1];
                var id = buildComponentId(componentName, part);
                var modelURL = buildURL(id, modelHash);
                var stylesURL = buildURL(id, stylesHash);
                if (modelURL) {
                    componentBuilder.addPart(id, part, modelURL);
                }
                if (stylesURL) {
                    componentBuilder.addStyleAsset(stylesURL, part);
                }
            });
        });
    }
    if (manifest.batches) {
        var batches = manifest.batches;
        Object.entries(batches).forEach(function (_a) {
            var batchId = _a[0], batch = _a[1];
            var v2 = batch.url_v2 && batch.url_v2.length;
            var id = buildBatchId(batchId);
            var id_v2 = v2
                ? editor_elements_conventions_1.getBundleId({
                    part: batchId,
                    namespace: manifest.namespace,
                    host: manifest.host,
                })
                : id;
            var model = v2
                ? extractURLs(batch.url_v2, manifest.shared)
                : extractURLs(batch.url, manifest.shared);
            var modelURL = buildURL(id_v2, model.js);
            var stylesURL = buildURL(id_v2, model.css);
            var addComponentBatchPart = function (_a) {
                var componentName = _a.componentName, part = _a.part;
                addBatchPart(hostBuilder, {
                    componentName: componentName,
                    part: part,
                    model: {
                        id: id,
                        js: modelURL,
                        css: stylesURL,
                    },
                });
            };
            if (batch.parts && batch.parts.length) {
                batch.parts.forEach(function (_a) {
                    var part = _a[0], components = _a.slice(1);
                    components.forEach(function (componentName) {
                        addComponentBatchPart({
                            componentName: componentName,
                            part: part,
                        });
                    });
                });
            }
            batch.components.forEach(function (componentName) {
                var _a;
                addComponentModels(hostBuilder, {
                    componentName: componentName,
                    model: {
                        id: id,
                        js: buildURL(id_v2, model.js),
                        css: buildURL(id_v2, model.css),
                        deps: (_a = model.deps) === null || _a === void 0 ? void 0 : _a.map(withBaseUrl),
                    },
                });
                if (batch.parts && batch.parts.length) {
                    batch.parts.forEach(function (_a) {
                        var part = _a[0];
                        addComponentBatchPart({
                            componentName: componentName,
                            part: part,
                        });
                    });
                }
            });
        });
    }
    return libraryBuilder.build();
}
exports.parseRegistryManifest = parseRegistryManifest;
//# sourceMappingURL=parseRegistryManifest.js.map